import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Heart, Sparkles, Users, Leaf } from "lucide-react";
import { Reveal } from "@/components/Motion";
import { MaskLogo, CloudBlob } from "@/components/Mask";

const VALUES = [
  { icon: Heart, title: "Good Food", desc: "Authentic recipes, quality ingredients, and flavour that speaks for itself." },
  { icon: Sparkles, title: "Warm Vibes", desc: "A friendly, buzzing space where everyone feels right at home." },
  { icon: Users, title: "For Everyone", desc: "Korea for K-fans and first-timers alike — veg and non-veg, all welcome." },
  { icon: Leaf, title: "Fresh Always", desc: "Made-to-order street food, quick and never compromised." },
];

const JOURNEY = [
  { year: "The Spark", text: "A love for Seoul's electric street-food markets and a dream to share it with India." },
  { year: "Sarjapur", text: "Our first home opens in Bangalore — bold flavours, warm hospitality, instant fans." },
  { year: "4.5 / 5", text: "Hundreds of happy reviews later, Koreyaah becomes a neighbourhood favourite." },
  { year: "What's Next", text: "Growing through franchises to bring Korean street food to every city." },
];

export default function About() {
  return (
    <div data-testid="about-page" className="pt-28">
      <section className="relative overflow-hidden mx-auto max-w-7xl px-5 py-16 md:py-24">
        <CloudBlob className="pointer-events-none absolute right-0 top-10 h-40 w-72 opacity-10" color="#2D3290" />
        <Reveal><p className="text-sm font-semibold uppercase tracking-[0.3em] text-koreyaah-red">About Koreyaah</p></Reveal>
        <Reveal delay={0.05}>
          <h1 className="mt-4 max-w-4xl font-display text-5xl sm:text-6xl md:text-7xl font-bold uppercase tracking-tight text-koreyaah-indigo leading-[0.9]">
            Korean heart, <span className="text-koreyaah-red">Indian warmth</span>
          </h1>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-8 max-w-2xl text-lg md:text-xl leading-relaxed text-koreyaah-indigo/80">
            At Koreyaah, we bring the vibrant energy of Korea's street food culture straight to your plate. From crispy Korean Fried Chicken and spicy Tteokbokki to comforting bowls of Ramen and refreshing Korean drinks, every dish is crafted with authentic ingredients and a modern twist. We're not just another Korean restaurant — we're a space where the magic of Korean flavours meets Indian warmth, making Korean street food fun, fast and unforgettable.
          </p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-8">
        <Reveal>
          <div className="overflow-hidden rounded-[2.5rem] shadow-pop">
            <img src="https://images.unsplash.com/photo-1552611052-33e04de081de?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80" alt="Korean cuisine" loading="lazy" className="h-[320px] w-full object-cover md:h-[520px]" />
          </div>
        </Reveal>
      </section>

      {/* Mission & Vision */}
      <section className="mx-auto max-w-7xl px-5 py-20 md:py-28 grid gap-8 md:grid-cols-2">
        <Reveal>
          <div className="h-full rounded-[2rem] bg-koreyaah-cobalt p-10 text-koreyaah-cream">
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-koreyaah-red">Mission</p>
            <p className="mt-5 font-display text-3xl md:text-4xl font-semibold leading-tight">To make Korean cuisine more accessible and exciting for everyone.</p>
          </div>
        </Reveal>
        <Reveal delay={0.08}>
          <div className="h-full rounded-[2rem] border-2 border-koreyaah-indigo/15 bg-white p-10">
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-koreyaah-red">Vision</p>
            <p className="mt-5 font-display text-3xl md:text-4xl font-semibold leading-tight text-koreyaah-indigo">To be that friendly spot where both Korean food fans and first-timers feel right at home.</p>
          </div>
        </Reveal>
      </section>

      {/* Journey */}
      <section className="bg-koreyaah-ghost py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5">
          <Reveal><p className="text-sm font-semibold uppercase tracking-[0.3em] text-koreyaah-red">The Journey</p></Reveal>
          <Reveal delay={0.05}><h2 className="mt-3 font-display text-4xl md:text-6xl font-semibold uppercase tracking-tight text-koreyaah-indigo">From Seoul to Sarjapur</h2></Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-4">
            {JOURNEY.map((j, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="h-full rounded-[2rem] bg-white border-2 border-koreyaah-indigo/10 p-8">
                  <span className="font-display text-2xl font-bold text-koreyaah-red">{j.year}</span>
                  <p className="mt-3 text-sm leading-relaxed text-koreyaah-indigo/75">{j.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="mx-auto max-w-7xl px-5 py-20 md:py-28">
        <Reveal><p className="text-sm font-semibold uppercase tracking-[0.3em] text-koreyaah-red">Brand Values</p></Reveal>
        <Reveal delay={0.05}><h2 className="mt-3 max-w-3xl font-display text-4xl md:text-6xl font-semibold uppercase tracking-tight text-koreyaah-indigo leading-[0.95]">What we stand for</h2></Reveal>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.06}>
              <div className="h-full rounded-[2rem] border-2 border-koreyaah-indigo/10 bg-white p-8 transition-transform duration-300 hover:-translate-y-1">
                <div className="grid h-14 w-14 place-items-center rounded-2xl bg-koreyaah-red text-white"><v.icon size={26} /></div>
                <h3 className="mt-6 font-display text-xl font-semibold text-koreyaah-indigo">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-koreyaah-indigo/70">{v.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Philosophy CTA */}
      <section className="relative overflow-hidden bg-koreyaah-red py-24 text-center text-koreyaah-cream">
        <div className="relative mx-auto max-w-4xl px-5">
          <MaskLogo className="mx-auto h-16 w-16" variant="white" />
          <h2 className="mt-6 font-display text-4xl md:text-6xl font-bold uppercase tracking-tight leading-[0.9]">Wow, you've got to try this!</h2>
          <p className="mx-auto mt-5 max-w-xl text-lg text-koreyaah-cream/85">It's all about good food, warm vibes, and those unforgettable moments.</p>
          <Link to="/menu" data-testid="about-cta-menu" className="mt-9 inline-flex items-center gap-2 rounded-full bg-koreyaah-cream px-9 py-4 text-base font-bold uppercase tracking-wide text-koreyaah-red transition-transform duration-200 hover:-translate-y-1">See the Menu <ArrowRight size={20} /></Link>
        </div>
      </section>
    </div>
  );
}
